import {
  awardCriteriaPlan,
  currentValueAt,
} from "./confirmationCardFormatting";
import { sameNumber } from "./statedWeightTotal";

type Row = Record<string, unknown>;

const AMOUNT = String.raw`(\d{1,3}(?:[ \u00a0\u202f]\d{3})*(?:[.,]\d+)?|\d+(?:[.,]\d+)?)\s*(%|prosent|kr|NOK)(?![\p{L}])`;
const STATED_BEFORE = new RegExp(
  String.raw`(?<![\p{L}])fra\s+` + AMOUNT,
  "giu",
);
const LIST_CONTINUATION = new RegExp(
  String.raw`(?:\s*,\s*|\s+og\s+)` + AMOUNT,
  "iuy",
);

const NOT_CHANGES = new Set(["fieldSources", "expectedCurrent"]);

export const statedBeforeValues = (text: string): number[] =>
  [...text.matchAll(STATED_BEFORE)].flatMap((match) =>
    statedList(text, match).amounts.map((amount) =>
      beforeNumber(amount.digits),
    ),
  );

type StatedAmount = { digits: string; digitsAt: number; unit: string };

const statedList = (
  text: string,
  first: RegExpMatchArray,
): { amounts: StatedAmount[]; end: number } => {
  const at = first.index ?? 0;
  const amounts = [
    { digits: first[1], digitsAt: at + first[0].search(/\d/), unit: first[2] },
  ];
  let end = at + first[0].length;
  LIST_CONTINUATION.lastIndex = end;
  for (
    let next = LIST_CONTINUATION.exec(text);
    next;
    next = LIST_CONTINUATION.exec(text)
  ) {
    amounts.push({
      digits: next[1],
      digitsAt: next.index + next[0].search(/\d/),
      unit: next[2],
    });
    end = LIST_CONTINUATION.lastIndex;
  }
  return { amounts, end };
};

export const statesBeforeValueNotOnCard = (
  summary: string | null,
  patch: Row,
  current: Row | undefined,
): boolean => {
  if (!summary || !current) return false;
  const stated = statedBeforeValues(summary);
  if (stated.length === 0) return false;
  const stored = touchedLeaves(patch, []).flatMap((path) =>
    numbersIn(currentValueAt(current, path)),
  );
  return stated.some(
    (value) => !stored.some((number) => sameNumber(number, value)),
  );
};

const touchedLeaves = (node: Row, at: string[]): string[][] =>
  Object.entries(node)
    .filter(
      ([key, value]) =>
        value !== undefined && !(at.length === 0 && NOT_CHANGES.has(key)),
    )
    .flatMap(([key, value]) =>
      isRow(value) && Object.keys(value).length > 0
        ? touchedLeaves(value, [...at, key])
        : [[...at, key]],
    );

const numbersIn = (value: unknown): number[] => {
  if (typeof value === "number") return [value];
  if (Array.isArray(value)) return value.flatMap(numbersIn);
  if (isRow(value)) return Object.values(value).flatMap(numbersIn);
  return [];
};

const isRow = (value: unknown): value is Row =>
  !!value && typeof value === "object" && !Array.isArray(value);

type CardWeight = {
  label: string;
  before: number;
  beforeDigits: string;
  after: number | null;
};

type TranslateEnum = (value: string) => string | undefined;

const AWARD_CRITERIA = ["eventData", "awardCriteria"];
const UP = /(?<![\p{L}])(?:opp|øker|økes|heves|høyere)(?![\p{L}])/iu;
const DOWN = /(?<![\p{L}])(?:ned|reduseres|senkes|synker|lavere)(?![\p{L}])/iu;
const CLAUSE_BREAK = /[!?;:\n]|[.,](?!\d)/gu;
const SENTENCE_BREAK = /[!?;:\n]|\.(?!\d)/gu;
const SENTENCES = /((?<=[.!?])\s+|\n+)/u;

export const withCardBeforeValues = (
  text: string,
  patch: Row,
  current: Row | undefined,
  translateEnum?: TranslateEnum,
): string => {
  if (!current || statedBeforeValues(text).length === 0) return text;
  const stored = touchedLeaves(patch, []).flatMap((path) =>
    numbersIn(currentValueAt(current, path)),
  );
  const weights = cardWeights(patch, current, translateEnum);
  return withEachSentence(text, (sentence) =>
    sentenceWithCardBeforeValues(sentence, stored, weights),
  );
};

export const withEachSentence = (
  text: string,
  rewrite: (sentence: string) => string | null,
): string => {
  const pieces = text.split(SENTENCES);
  const kept: string[] = [];
  for (let index = 0; index < pieces.length; index += 2) {
    const sentence = rewrite(pieces[index]);
    if (sentence !== null) kept.push(sentence + (pieces[index + 1] ?? ""));
  }
  const fixed = kept.join("");
  return fixed === text ? text : fixed.trim();
};

const sentenceWithCardBeforeValues = (
  sentence: string,
  stored: number[],
  weights: CardWeight[],
): string | null => {
  let result = "";
  let clauseFrom = 0;
  const corrected: CardWeight[] = [];
  for (const match of sentence.matchAll(STATED_BEFORE)) {
    const at = match.index ?? 0;
    const { amounts, end } = statedList(sentence, match);
    const stated = amounts.map((amount) => beforeNumber(amount.digits));
    const isWeight = amounts.every((amount) =>
      /^(?:%|prosent)$/iu.test(amount.unit),
    );
    const named = isWeight
      ? weightsNamedBefore(sentence, clauseFrom, at, stated.length, weights)
      : null;
    result += sentence.slice(clauseFrom, at);
    clauseFrom = end;
    if (
      named &&
      named.weights.some(
        (weight, index) => !sameNumber(stated[index], weight.before),
      )
    ) {
      if (
        named.weights.some((weight) => contradictsDirection(named.lead, weight))
      )
        return null;
      corrected.push(...named.weights);
      let rewritten = "";
      let from = at;
      amounts.forEach((amount, index) => {
        rewritten +=
          sentence.slice(from, amount.digitsAt) +
          named.weights[index].beforeDigits;
        from = amount.digitsAt + amount.digits.length;
      });
      result += rewritten + sentence.slice(from, end);
    } else if (
      named ||
      stated.every((value) =>
        stored.some((number) => sameNumber(number, value)),
      )
    ) {
      result += sentence.slice(at, end);
    } else {
      result = result.trimEnd();
      if (result === "")
        clauseFrom =
          sentence.length - sentence.slice(clauseFrom).trimStart().length;
    }
  }
  const rewritten = result + sentence.slice(clauseFrom);
  return corrected.length > 0 &&
    statesSizeOfChangeNotOnCard(rewritten, corrected)
    ? null
    : rewritten;
};

const SIZE_OF_CHANGE =
  /(\d+(?:[.,]\d+)?)\s*(?:prosentpoeng|prosentpoenger|%-poeng|poeng)(?![\p{L}])/giu;
const HALVED = /(?<![\p{L}])halver(?:es|t|er)(?![\p{L}])/iu;
const DOUBLED = /(?<![\p{L}])dobl(?:es|et|er)(?![\p{L}])/iu;

const statesSizeOfChangeNotOnCard = (
  sentence: string,
  weights: CardWeight[],
): boolean => {
  const moves = weights.flatMap((weight) =>
    weight.after === null ? [] : [Math.abs(weight.after - weight.before)],
  );
  const statedMoves = [...sentence.matchAll(SIZE_OF_CHANGE)].map((match) =>
    beforeNumber(match[1]),
  );
  if (
    statedMoves.some(
      (stated) => !moves.some((move) => sameNumber(move, stated)),
    )
  )
    return true;
  if (
    HALVED.test(sentence) &&
    !weights.some(
      (weight) =>
        weight.after !== null && sameNumber(weight.after * 2, weight.before),
    )
  )
    return true;
  return (
    DOUBLED.test(sentence) &&
    !weights.some(
      (weight) =>
        weight.after !== null && sameNumber(weight.after, weight.before * 2),
    )
  );
};

const weightsNamedBefore = (
  sentence: string,
  from: number,
  to: number,
  count: number,
  weights: CardWeight[],
): { lead: string; weights: CardWeight[] } | null => {
  for (const pattern of [CLAUSE_BREAK, SENTENCE_BREAK]) {
    const lead = sentence.slice(clauseStart(sentence, from, to, pattern), to);
    const named = weightsNamedIn(lead, weights);
    if (named.length === count) return { lead, weights: named };
  }
  return null;
};

const clauseStart = (
  sentence: string,
  from: number,
  to: number,
  pattern: RegExp,
): number => {
  const breaks = [...sentence.slice(from, to).matchAll(pattern)];
  const last = breaks[breaks.length - 1];
  return last ? from + (last.index ?? 0) + 1 : from;
};

const weightsNamedIn = (lead: string, weights: CardWeight[]): CardWeight[] => {
  const named = weights.filter((weight) =>
    namePattern(weight.label).test(lead),
  );
  return named
    .filter(
      (weight) =>
        !named.some(
          (other) =>
            other.label.length > weight.label.length &&
            namePattern(weight.label).test(other.label),
        ),
    )
    .sort(
      (a, b) =>
        lead.search(namePattern(a.label)) - lead.search(namePattern(b.label)),
    );
};

export const namePattern = (label: string, flags = "iu"): RegExp =>
  new RegExp(
    `(?<![\\p{L}])${label.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![\\p{L}])`,
    flags,
  );

const contradictsDirection = (lead: string, weight: CardWeight): boolean => {
  if (weight.after === null) return false;
  if (UP.test(lead)) return !(weight.after > weight.before);
  if (DOWN.test(lead)) return !(weight.after < weight.before);
  return false;
};

const cardWeights = (
  patch: Row,
  current: Row,
  translateEnum?: TranslateEnum,
): CardWeight[] => {
  const proposed = AWARD_CRITERIA.reduce<unknown>(
    (node, key) => (isRow(node) ? node[key] : undefined),
    patch,
  );
  if (!isRow(proposed)) return [];
  const plan = awardCriteriaPlan(
    proposed,
    currentValueAt(current, AWARD_CRITERIA),
    { translateEnum },
  );
  if (plan.kind !== "bestPriceQualityRatio") return [];
  return plan.rows.flatMap((row) => {
    const beforeDigits = row.previousWeight?.match(/^(\S+) %$/u)?.[1];
    if (!beforeDigits || !row.label.trim()) return [];
    const after = row.weight?.match(/^(\S+) %$/u)?.[1];
    return [
      {
        label: row.label,
        before: beforeNumber(beforeDigits),
        beforeDigits,
        after: after ? beforeNumber(after) : null,
      },
    ];
  });
};

const beforeNumber = (digits: string): number =>
  Number(digits.replace(/[ \u00a0\u202f]/g, "").replace(",", "."));
